import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { getEquivalentRoute } from '../lib/i18n'

export default function Footer() {
  const location = useLocation()
  const navigate = useNavigate()
  const isEn = location.pathname.startsWith('/en')

  const handleLanguageSwitch = (targetLang) => {
    localStorage.setItem('preferred_lang', targetLang)
    const targetPath = getEquivalentRoute(location.pathname, targetLang)
    navigate(targetPath)
  }

  return (
    <footer className="bg-white text-slate-600 pt-16 pb-12 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 mb-12">
          
          {/* Col 1: Brand Info */}
          <div className="space-y-4">
            <Link to={isEn ? '/en' : '/'} className="inline-block">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=90,format=auto/assets/44bca6c4-b08c-481d-a28a-ebff52067c25/imgi-3-logo-18.png" 
                alt="Borgs Køreskole logo" 
                className="h-14 sm:h-18 w-auto object-contain"
              />
            </Link>
            <p className="text-xs text-slate-500 leading-relaxed">
              {isEn 
                ? 'Borgs Driving School I/S in Kalundborg – Personal and qualified driving instruction in safe and supportive surroundings.'
                : 'Borgs Køreskole I/S i Kalundborg – Personlig og kvalificeret køreundervisning i trygge rammer.'
              }
            </p>

            {/* Language Switcher inside footer brand col */}
            <div className="pt-2">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                {isEn ? 'Language / Sprog' : 'Sprog / Language'}
              </span>
              <div className="inline-flex items-center gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => handleLanguageSwitch('da')}
                  className={`px-2.5 py-1 rounded transition-colors flex items-center gap-1.5 cursor-pointer ${
                    !isEn ? 'bg-white text-emerald-800 font-bold shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <span>🇩🇰</span>
                  <span>Dansk</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleLanguageSwitch('en')}
                  className={`px-2.5 py-1 rounded transition-colors flex items-center gap-1.5 cursor-pointer ${
                    isEn ? 'bg-white text-emerald-800 font-bold shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <span>🇬🇧</span>
                  <span>English</span>
                </button>
              </div>
            </div>
          </div>

          {/* Col 2: Navigation (Sider & Information) */}
          <div>
            <h3 className="font-header text-slate-900 font-bold mb-4 tracking-wide uppercase text-xs">
              {isEn ? 'Pages & Information' : 'Sider & Information'}
            </h3>
            <ul className="space-y-2 text-xs font-semibold">
              <li>
                <Link to={isEn ? '/en/mandatory-package-prices' : '/lovpakke-priser'} className="hover:text-emerald-600 transition-colors inline-flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-emerald-500" aria-hidden="true"></i> {isEn ? 'Mandatory Package & Prices' : 'Lovpakke & Priser'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/theory-classes' : '/teori-undervisning'} className="hover:text-emerald-600 transition-colors inline-flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-emerald-500" aria-hidden="true"></i> {isEn ? 'Theory & Lessons' : 'Teori & Undervisning'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/links' : '/links'} className="hover:text-emerald-600 transition-colors inline-flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-emerald-500" aria-hidden="true"></i> {isEn ? 'Useful Links' : 'Links'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/about-us' : '/om-os'} className="hover:text-emerald-600 transition-colors inline-flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-emerald-500" aria-hidden="true"></i> {isEn ? 'About Borgs Driving School' : 'Om Borgs Køreskole'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/testimonials' : '/anbefalinger'} className="hover:text-emerald-600 transition-colors inline-flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-emerald-500" aria-hidden="true"></i> {isEn ? 'Testimonials' : 'Anbefalinger'}
                </Link>
              </li>
              <li>
                <a 
                  href="https://www.lendo.dk/partner/borgs-koreskole" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-emerald-600 transition-colors inline-flex items-center gap-2 text-emerald-700"
                >
                  <i className="fa-solid fa-chevron-right text-[10px] text-emerald-500" aria-hidden="true"></i> {isEn ? 'Financing' : 'Finansiering'}
                  <i className="fa-solid fa-arrow-up-right-from-square text-[9px] text-slate-400" aria-hidden="true"></i>
                </a>
              </li>
              <li>
                <Link to={isEn ? '/en/terms' : '/betingelser'} className="hover:text-emerald-600 transition-colors inline-flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-emerald-500" aria-hidden="true"></i> {isEn ? 'Terms & Conditions' : 'Betingelser'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/contact' : '/kontakt'} className="hover:text-emerald-600 transition-colors inline-flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-emerald-500" aria-hidden="true"></i> {isEn ? 'Contact' : 'Kontakt'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Contact details */}
          <div>
            <h3 className="font-header text-slate-900 font-bold mb-4 tracking-wide uppercase text-xs">
              {isEn ? 'Contact Information' : 'Kontaktoplysninger'}
            </h3>
            <ul className="space-y-3 text-xs">
              <li className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot text-emerald-600 mt-1" aria-hidden="true"></i>
                <div>
                  <strong className="text-slate-900 block">{isEn ? 'Address:' : 'Adresse:'}</strong>
                  <span>Elmegade 19<br />4400 Kalundborg</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="fa-solid fa-phone text-emerald-600 mt-1" aria-hidden="true"></i>
                <div>
                  <strong className="text-slate-900 block">{isEn ? 'Phone:' : 'Telefon:'}</strong>
                  <div className="flex flex-col space-y-0.5">
                    <a href="tel:21470187" className="hover:text-emerald-600 transition-colors whitespace-nowrap">
                      21 47 01 87
                    </a>
                    <a href="tel:23200425" className="hover:text-emerald-600 transition-colors whitespace-nowrap">
                      23 20 04 25
                    </a>
                  </div>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-emerald-600" aria-hidden="true"></i>
                <div>
                  <strong className="text-slate-900 block">Email:</strong>
                  <a href="mailto:info@borgskoereskole.dk" className="hover:text-emerald-600 transition-colors whitespace-nowrap">
                    info@borgskoereskole.dk
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Col 4: Business details */}
          <div>
            <h3 className="font-header text-slate-900 font-bold mb-4 tracking-wide uppercase text-xs">
              {isEn ? 'Company Information' : 'Virksomhedsoplysninger'}
            </h3>
            <ul className="space-y-2 text-xs">
              <li>
                <strong className="text-slate-900">CVR:</strong> 43296000
              </li>
              <li className="pt-2 text-slate-500 leading-relaxed">
                {isEn 
                  ? 'Lessons take place during weekdays and weekends according to agreement with the instructor.'
                  : 'Undervisning finder sted i hverdage og weekender efter aftale med kørelæreren.'
                }
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom copyright */}
        <div className="border-t border-slate-200 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} Borgs Køreskole I/S. {isEn ? 'All rights reserved.' : 'Alle rettigheder forbeholdes.'}</p>
          <div className="flex items-center gap-6">
            <Link to={isEn ? '/en/terms' : '/betingelser'} className="hover:text-emerald-600 transition-colors">
              {isEn ? 'Terms & Conditions' : 'Forretningsbetingelser'}
            </Link>
            <Link to={isEn ? '/en/contact' : '/kontakt'} className="hover:text-emerald-600 transition-colors">
              {isEn ? 'Contact' : 'Kontakt'}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
